import { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/Modal';
import Button from 'components/UI/Button';
import Switch from 'components/UI/Switch';
import { ReactComponent as DocumentIcon } from 'assets/icons/digital-loan-document.svg';
import { ReactComponent as IdCardIcon } from 'assets/icons/digital-loan-id-card.svg';
import { ReactComponent as CameraIcon } from 'assets/icons/camera.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/digital-loan-info.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/digital-loan-check.svg';

const PhotoRequirementsScreen = ({
  literals,
  consents,
  onConsentChange,
  onContinue,
  onBack,
}) => {
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [continueAfterRecommendations, setContinueAfterRecommendations] = useState(false);

  const canContinue = consents.isOwner && consents.cameraAuthorized;

  const openRecommendations = (shouldContinue) => {
    setContinueAfterRecommendations(shouldContinue);
    setShowRecommendations(true);
  };

  const handleUnderstood = () => {
    const shouldContinue = continueAfterRecommendations;
    setShowRecommendations(false);
    setContinueAfterRecommendations(false);
    if (shouldContinue) {
      onContinue();
    }
  };

  return (
    <div className='full-screen-container digital-loan-photo-requirements'>
      <div className='screen-content'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>
        <p className='digital-loan-phase2__body'>{literals.intro1}</p>
        <p className='digital-loan-phase2__body mb-4'>{literals.intro2}</p>

        <div className='digital-loan-photo-requirements__heading'>
          <p className='digital-loan-photo-requirements__section'>
            {literals.requiredTitle}
          </p>
          <button
            type='button'
            className='digital-loan-photo-requirements__info'
            onClick={() => openRecommendations(false)}
            aria-label={literals.recommendationsTitle}
          >
            <InfoIcon aria-hidden='true' />
          </button>
        </div>
        <ul className='digital-loan-photo-requirements__list'>
          <li className='digital-loan-photo-requirements__list-item'>
            <CheckIcon className='digital-loan-photo-requirements__check' aria-hidden='true' />
            <span>{literals.requiredFront}</span>
          </li>
          <li className='digital-loan-photo-requirements__list-item'>
            <CheckIcon className='digital-loan-photo-requirements__check' aria-hidden='true' />
            <span>{literals.requiredAngles}</span>
          </li>
          <li className='digital-loan-photo-requirements__list-item'>
            <CheckIcon className='digital-loan-photo-requirements__check' aria-hidden='true' />
            <span>
              {literals.requiredLabel}
              <span className='digital-loan-photo-requirements__hint'>
                {' '}
                {literals.requiredLabelHint}
              </span>
            </span>
          </li>
          <li className='digital-loan-photo-requirements__list-item'>
            <CheckIcon className='digital-loan-photo-requirements__check' aria-hidden='true' />
            <span>
              {literals.optionalDocument}
              <span className='digital-loan-photo-requirements__hint'>
                {' '}
                {literals.optionalDocumentHint}
              </span>
            </span>
          </li>
        </ul>

        <div className='digital-loan-photo-requirements__toggles'>
          <div className='digital-loan-photo-requirements__toggle-row'>
            <DocumentIcon className='digital-loan-photo-requirements__icon' aria-hidden='true' />
            <span>{literals.hasInvoice}</span>
            <Switch
              checked={consents.hasInvoice}
              onChange={() => onConsentChange('hasInvoice')}
            />
          </div>
          <div className='digital-loan-photo-requirements__toggle-row'>
            <IdCardIcon className='digital-loan-photo-requirements__icon' aria-hidden='true' />
            <span>{literals.isOwner}</span>
            <Switch
              checked={consents.isOwner}
              onChange={() => onConsentChange('isOwner')}
            />
          </div>
          <div className='digital-loan-photo-requirements__toggle-row'>
            <CameraIcon className='digital-loan-photo-requirements__icon' aria-hidden='true' />
            <span>{literals.cameraAuthorized}</span>
            <Switch
              checked={consents.cameraAuthorized}
              onChange={() => onConsentChange('cameraAuthorized')}
            />
          </div>
        </div>
      </div>
      <div className='screen-actions'>
        <Button
          text={literals.takePhotos}
          disabled={!canContinue}
          onClick={() => openRecommendations(true)}
        />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>

      {showRecommendations && (
        <Modal
          className='digital-loan-recommendations-modal'
          position='center'
          closeOnBackgroundClick={!continueAfterRecommendations}
          showCloseButton={false}
          onClose={() => {
            if (!continueAfterRecommendations) {
              setShowRecommendations(false);
            }
          }}
        >
          <div className='digital-loan-recommendations-modal__content'>
            <h2 className='digital-loan-recommendations-modal__title'>
              {literals.recommendationsTitle}
            </h2>
            <ul className='digital-loan-recommendations-modal__list'>
              {(literals.recommendations || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Button
              text={literals.understood}
              onClick={handleUnderstood}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

PhotoRequirementsScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  consents: PropTypes.shape({
    hasInvoice: PropTypes.bool,
    isOwner: PropTypes.bool,
    cameraAuthorized: PropTypes.bool,
  }).isRequired,
  onConsentChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default PhotoRequirementsScreen;
